import type { AgentsBoard, ProposalKind } from '../../api/agentSchemas';
import { spoken, type AgentLine } from './agentLine';
import type { FinanceFacts, FinanceNeed } from './finance/facts';
import { financeNeedsOf, financeReplyFor, isFinanceId, type FinanceIntentId } from './finance/intents';
import type { IntentId } from './router/taxonomy';
import type { LineId } from './voice/lines';
import { aboutKind, aboutProposal, firstThing, greeting, moreClients } from './work';

/**
 * What the team says for each situation the router can name. v1 stays deterministic on purpose:
 * every reply is one of the recorded lines in voice/lines.ts, and what changes from one business to
 * the next (numbers, the work waiting) travels beside it as a note or as figures.
 */

export type ReplyContext = {
  board: AgentsBoard;
  /** The numbers of the business, when the situation needs them (see `financeNeedsFor`). */
  finance: FinanceFacts;
  now: Date;
  timeZone?: string;
  /** The kind of work the sentence is about, when it names one (see `namedProposalKind`): a reaction is about that work. */
  named?: ProposalKind | null;
};

type Handler = (context: ReplyContext) => AgentLine;

const money =
  (id: FinanceIntentId): Handler =>
  ({ finance, board }) =>
    financeReplyFor(id, finance, board) ?? firstThing(board);

const line =
  (voz: LineId): Handler =>
  () =>
    spoken('direccion', voz);

const reaction =
  (voz: LineId): Handler =>
  ({ board, named }) =>
    aboutProposal(board, voz, named);

const HANDLERS: Readonly<Record<IntentId, Handler>> = {
  ventas: money('ventas'),
  hoy: money('hoy'),
  ayer: money('ayer'),
  semana: money('semana'),
  'mes-pasado': money('mes-pasado'),
  anio: money('anio'),
  comparar: money('comparar'),
  resultado: money('resultado'),
  gastos: money('gastos'),
  'mayor-gasto': money('mayor-gasto'),
  planilla: money('planilla'),
  'gastos-fijos': money('gastos-fijos'),
  'por-cobrar': money('por-cobrar'),
  metodo: money('metodo'),
  servicio: money('servicio'),
  equipo: money('equipo'),
  cliente: money('cliente'),
  ticket: money('ticket'),
  faltas: money('faltas'),
  impuesto: money('impuesto'),
  resumen: money('resumen'),
  'sin-info': money('sin-info'),
  mejorar: money('mejorar'),
  'finanzas-otro': money('finanzas-otro'),

  oferta: ({ board }) => aboutKind(board, 'offer'),
  winback: ({ board }) => aboutKind(board, 'win_back'),
  'mas-clientes': ({ board }) => moreClients(board),
  'marketing-otro': ({ board }) => moreClients(board),

  confirmar: ({ board }) => aboutKind(board, 'confirm_visits'),
  'operaciones-otro': ({ board }) => aboutKind(board, 'confirm_visits'),

  primero: ({ board }) => firstThing(board),
  puedo: line('puedo'),
  'quien-eres': line('quien-eres'),
  'direccion-otro': line('puedo'),

  'propuesta-rechazar': reaction('propuesta-rechazar'),
  'propuesta-aprobar': reaction('propuesta-aprobar'),
  'propuesta-cambiar': reaction('propuesta-cambiar'),
  'propuesta-porque': reaction('propuesta-porque'),
  'propuesta-otra': reaction('propuesta-otra'),
  'propuesta-despues': reaction('propuesta-despues'),
  'propuestas-otro': reaction('propuesta-cambiar'),

  saludo: ({ board, now, timeZone }) => greeting(board, now, timeZone),
  gracias: line('de-nada'),
  despedida: line('hasta-luego'),
  elogio: line('gracias-elogio'),
  queja: line('perdon-queja'),

  accion: line('no-hago'),
  fuera: line('nosabe'),
};

/** The reply for a situation the router named. */
export function replyFor(intent: IntentId, context: ReplyContext): AgentLine {
  return HANDLERS[intent](context);
}

/** The numbers to fetch before answering this situation, or null when it needs none. */
export function financeNeedsFor(intent: IntentId): readonly FinanceNeed[] | null {
  return isFinanceId(intent) ? financeNeedsOf(intent) : null;
}
